import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { prepareHeaders } from './index';
import { API_ICARDIO } from 'constants/env.const';
import {
  IAnnotationSet,
  IAnnotationSetsParams,
  IDicom,
  IDismissReviewFrameParams,
  IDicomLabelingJob,
  IDicomLabelingJobsParams,
  IDicomsParams,
  IFrame,
  IFrameParams,
  IKeypointCollectionClass,
  IKeypointLabel,
  IPagination,
  IPhaseClass,
  IPhaseLabel,
  IQualityLabel,
  IQualityLabelsParams,
  ISegmentationClass,
  ISegmentationLabel,
  ISegmentationPrediction,
  ISegmentationReference,
  IStudy,
  IStudyLabelingJob,
  IStudyLabelingJobsParams,
  IViewClass,
  IViewLabel,
  IViewLabelsParams,
  IPhaseLabelsParams,
  IKeypointCollectionLabelsParams,
  IKeypointCollectionLabel,
  ISegmentationLabelsParams,
  IReviewFrame,
  IReviewFramesParams,
  IUpdateReviewFrameStatusParams,
  ISegmentationReferencesParams,
  ISegmentationPredictionsParams,
  IUsersParams,
  IPnoFrame,
  IPnoParams,
  IPnoExistsParams,
  IPnoExistsResponse,
  IViewPredictionsSummary,
} from 'models/icardio';
import { IUser } from 'models/auth';

export const icardioAPI = createApi({
  reducerPath: 'icardioAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: API_ICARDIO,
    prepareHeaders: prepareHeaders,
    credentials: 'include',
  }),
  tagTypes: [
    'FullAnnotationSets',
    'ViewClasses',
    'PhaseClasses',
    'KeypointCollectionClasses',
    'SegmentationClasses',
    'StudyLabelingJob',
    'Dicom',
    'DicomLabelingJob',
    'ViewLabels',
    'PhaseLabels',
    'KeypointCollectionLabels',
    'SegmentationLabels',
    'Study',
    'QualityLabels',
    'SegmentationPredictions',
    'SegmentationReferences',
    'ReviewFrames',
    'Users',
  ],
  endpoints: builder => ({
    getFullAnnotationSets: builder.query<
      IPagination | IAnnotationSet[],
      IAnnotationSetsParams | void
    >({
      query: (params?: IAnnotationSetsParams) => ({
        url: `/api/v1/full-study-annotation-sets/`,
        method: 'GET',
        params: {
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
          ...(params?.userEmail !== undefined && { user_email: params.userEmail }),
        },
      }),
      providesTags: [{ type: 'FullAnnotationSets', id: 'LIST' }],
    }),
    getFullAnnotationSet: builder.query<IAnnotationSet, string>({
      query: id => ({
        url: `/api/v1/full-study-annotation-sets/${id}/`,
        method: 'GET',
      }),
      providesTags: [{ type: 'FullAnnotationSets', id: 'LIST' }],
    }),
    getFullAnnotationSetsSearch: builder.query({
      query: ({ patient_id, snapshot_id, frame_id }) => {
        let query = '/api/v1/full-study-annotation-sets/search?';

        if (patient_id) query += `patient_id=${patient_id}&`;
        if (snapshot_id) query += `snapshot_id=${snapshot_id}&`;
        if (frame_id) query += `frame_id=${frame_id}&`;

        // remove the trailing "&" if there's one
        query = query.endsWith('&') ? query.slice(0, -1) : query;

        return {
          url: query,
          method: 'GET',
        };
      },
      providesTags: [{ type: 'FullAnnotationSets', id: 'LIST' }],
    }),
    getStudy: builder.query<IStudy, string>({
      query: (studyIcid: string) => ({
        url: `/api/v1/studies/${studyIcid}/`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Study', id: 'RETRIEVE' }],
    }),
    getViewClasses: builder.query<IViewClass[], void>({
      query: () => ({
        url: `/api/v1/view-classes/`,
        method: 'GET',
        params: { paginate: 'false' },
      }),
      providesTags: [{ type: 'ViewClasses', id: 'LIST' }],
    }),
    getPhaseClasses: builder.query<IPhaseClass[], void>({
      query: () => ({
        url: `/api/v1/phase-classes/`,
        method: 'GET',
        params: { paginate: 'false' },
      }),
      providesTags: [{ type: 'PhaseClasses', id: 'LIST' }],
    }),
    getKeypointCollectionClasses: builder.query<IKeypointCollectionClass[], void>({
      query: () => ({
        url: `/api/v1/keypoint-collection-classes/`,
        method: 'GET',
        params: { paginate: 'false' },
      }),
      providesTags: [{ type: 'KeypointCollectionClasses', id: 'LIST' }],
    }),
    getSegmentationClasses: builder.query<ISegmentationClass[], void>({
      query: () => ({
        url: `/api/v1/segmentation-classes/`,
        method: 'GET',
        params: { paginate: 'false' },
      }),
      providesTags: [{ type: 'SegmentationClasses', id: 'LIST' }],
    }),
    getStudyLabelingJobs: builder.query<
      IPagination | IStudyLabelingJob[],
      IStudyLabelingJobsParams | void
    >({
      query: (params?: IStudyLabelingJobsParams) => ({
        url: `/api/v1/study-labeling-jobs/`,
        method: 'GET',
        params: {
          ...(params?.annotationSetId !== undefined && { annotation_set: params.annotationSetId }),
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params?.userEmail !== undefined && { user_email: params.userEmail }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'StudyLabelingJob', id: 'LIST' }],
    }),
    getStudyLabelingJob: builder.query<IStudyLabelingJob, string>({
      query: (studyLabelingJobId: string) => ({
        url: `/api/v1/study-labeling-jobs/${studyLabelingJobId}/`,
        method: 'GET',
      }),
      providesTags: [{ type: 'StudyLabelingJob', id: 'RETRIEVE' }],
    }),
    getDicom: builder.query<IDicom, string>({
      query: (dicomIcid: string) => ({
        url: `/api/v1/dicoms/${dicomIcid}/`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Dicom', id: 'RETRIEVE' }],
    }),
    getDicoms: builder.query<IPagination | IDicom[], IDicomsParams | void>({
      query: (params?: IDicomsParams) => ({
        url: '/api/v1/dicoms/',
        method: 'GET',
        params: {
          ...(params?.frameIcid !== undefined && { frame_icid: params.frameIcid }),
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params?.pipeline !== undefined && { pipeline: params.pipeline }),
          ...(params?.viewClass !== undefined && { view_class: params.viewClass }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'Dicom', id: 'LIST' }],
    }),
    getFrames: builder.query<IFrame[], IFrameParams | void>({
      query: (params?: IFrameParams) => ({
        url: '/api/v1/frames/',
        method: 'GET',
        params: {
          ...(params?.study !== undefined && { study: params.study }),
          ...(params?.dicom !== undefined && { dicom: params.dicom }),
          ...(params?.dicomIcid !== undefined && { dicom_icid: params.dicomIcid }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'StudyLabelingJob', id: 'LIST' }],
    }),
    getDicomLabelingJobs: builder.query<
      IDicomLabelingJob[] | IPagination,
      IDicomLabelingJobsParams | void
    >({
      query: (params?: IDicomLabelingJobsParams) => ({
        url: `/api/v1/dicom-labeling-jobs/`,
        method: 'GET',
        params: {
          ...(params?.studyLabelingJobUuid !== undefined && {
            study_labeling_job: params.studyLabelingJobUuid,
          }),
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params?.dicomIcid !== undefined && { dicom_icid: params.dicomIcid }),
          ...(params?.pipeline !== undefined && { pipeline: params.pipeline }),
          ...(params?.viewClass !== undefined && { view_class: params.viewClass }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'DicomLabelingJob', id: 'LIST' }],
    }),
    upsertDicomJob: builder.mutation<
      { dicom: string; annotationSet: string; labeled: boolean; dicomLabelingJob: string },
      any
    >({
      query: args => ({
        url: `/api/v1/dicom-labeling-jobs/${args.dicomLabelingJob}/`,
        method: 'PATCH',
        body: { dicom: args.dicom, annotationSet: args.annotationSet, labeled: args.labeled },
      }),
    }),
    getViewLabels: builder.query<IPagination | IViewLabel[], IViewLabelsParams | void>({
      query: (params?: IViewLabelsParams) => ({
        url: '/api/v1/view-labels/',
        method: 'GET',
        params: {
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'ViewLabels', id: 'LIST' }],
    }),
    upsertViewLabel: builder.mutation<{ viewClass: string; dicom: string }, any>({
      query: args => ({
        url: `/api/v1/view-labels/`,
        method: 'POST',
        body: args,
      }),
      invalidatesTags: [{ type: 'ViewLabels', id: 'LIST' }],
    }),
    getPhaseLabels: builder.query<IPagination | IPhaseLabel[], IPhaseLabelsParams | void>({
      query: (params?: IPhaseLabelsParams) => ({
        url: '/api/v1/phase-labels/',
        method: 'GET',
        params: {
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'PhaseLabels', id: 'LIST' }],
    }),
    upsertPhaseLabels: builder.mutation<IPhaseLabel[], any>({
      query: args => ({
        url: `/api/v1/phase-labels/`,
        method: 'POST',
        body: args,
      }),
      invalidatesTags: [{ type: 'ViewLabels', id: 'LIST' }],
    }),
    getKeypointCollectionLabels: builder.query<
      IPagination | IKeypointCollectionLabel[],
      IKeypointCollectionLabelsParams | void
    >({
      query: (params?: IKeypointCollectionLabelsParams) => ({
        url: '/api/v1/keypoint-collection-labels/',
        method: 'GET',
        params: {
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'KeypointCollectionLabels', id: 'LIST' }],
    }),
    upsertKeypointCollectionLabels: builder.mutation<IKeypointLabel[], any>({
      query: args => ({
        url: `/api/v1/keypoint-collection-labels/`,
        method: 'POST',
        body: args,
      }),
      invalidatesTags: [{ type: 'KeypointCollectionLabels', id: 'LIST' }],
    }),
    getSegmentationLabel: builder.query<ISegmentationLabel, string>({
      query: id => ({
        url: `/api/v1/segmentation-labels/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'SegmentationLabels', id }],
    }),
    getSegmentationLabels: builder.query<
      IPagination | ISegmentationLabel[],
      ISegmentationLabelsParams | void
    >({
      query: (params?: ISegmentationLabelsParams) => ({
        url: '/api/v1/on-review-segmentation-labels/',
        method: 'GET',
        params: {
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params?.dicomIcid !== undefined && { dicom_icid: params.dicomIcid }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
          ...(params?.sourceIcid !== undefined && { source_icid: params.sourceIcid }),
        },
      }),
      providesTags: [{ type: 'SegmentationLabels', id: 'LIST' }],
    }),
    getReviewFrames: builder.query<IReviewFrame[], IReviewFramesParams>({
      query: params => ({
        url: '/api/v1/review-frames/',
        method: 'GET',
        params: {
          ...(params.annotationSetId !== undefined && {
            annotation_set: params.annotationSetId,
          }),
          ...(params.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params.dicomIcid !== undefined && { dicom_icid: params.dicomIcid }),
          ...(params.decision !== undefined && { decision: params.decision }),
          ...(params.status !== undefined && { status: params.status }),
          ...(params.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'ReviewFrames', id: 'LIST' }],
    }),
    dismissReviewFrame: builder.mutation<IReviewFrame, IDismissReviewFrameParams>({
      query: ({ dicomId, frameIndex, reviewerComment }) => ({
        url: '/api/v1/review-frames/',
        method: 'PATCH',
        body: {
          dicom_id: dicomId,
          frame_index: frameIndex,
          reviewer_comment: reviewerComment,
          status: 'DISMISSED',
        },
      }),
      invalidatesTags: [{ type: 'ReviewFrames', id: 'LIST' }],
    }),
    updateReviewFrameStatus: builder.mutation<IReviewFrame, IUpdateReviewFrameStatusParams>({
      query: ({ dicomId, frameIndex, status }) => ({
        url: '/api/v1/review-frames/',
        method: 'PATCH',
        body: {
          dicom_id: dicomId,
          frame_index: frameIndex,
          status,
        },
      }),
      invalidatesTags: [{ type: 'ReviewFrames', id: 'LIST' }],
    }),
    upsertSegmentationLabels: builder.mutation<ISegmentationLabel[], any>({
      query: args => ({
        url: `/api/v1/segmentation-labels/`,
        method: 'POST',
        body: args,
      }),
      invalidatesTags: [{ type: 'SegmentationLabels', id: 'LIST' }],
    }),
    getSegmentationPredictions: builder.query<
      IPagination | ISegmentationPrediction[],
      ISegmentationPredictionsParams | void
    >({
      query: (params?: ISegmentationPredictionsParams) => ({
        url: '/api/v1/segmentation-predictions/',
        method: 'GET',
        params: {
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params?.dicomIcid !== undefined && { dicom_icid: params.dicomIcid }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'SegmentationPredictions', id: 'LIST' }],
    }),
    getSegmentationReferences: builder.query<
      IPagination | ISegmentationReference[],
      ISegmentationReferencesParams | void
    >({
      query: (params?: ISegmentationReferencesParams) => ({
        url: '/api/v1/segmentation-references/',
        method: 'GET',
        params: {
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'SegmentationReferences', id: 'LIST' }],
    }),
    getQualityLabels: builder.query<IPagination | IQualityLabel[], IQualityLabelsParams | void>({
      query: (params?: IQualityLabelsParams) => ({
        url: '/api/v1/quality-labels/',
        method: 'GET',
        params: {
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'QualityLabels', id: 'LIST' }],
    }),
    upsertQualityLabels: builder.mutation<IQualityLabel[], any>({
      query: args => ({
        url: `/api/v1/quality-labels/`,
        method: 'POST',
        body: args,
      }),
      invalidatesTags: [{ type: 'QualityLabels', id: 'LIST' }],
    }),
    getPno: builder.query<IPnoFrame[], IPnoParams | void>({
      query: (params?: IPnoParams) => ({
        url: '/api/v1/pno/',
        method: 'GET',
        params: {
          ...(params?.dicomIcid !== undefined && { dicom_icid: params.dicomIcid }),
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
    }),
    getPnoExists: builder.query<IPnoExistsResponse, IPnoExistsParams | void>({
      query: (params?: IPnoExistsParams) => ({
        url: '/api/v1/pno-exists/',
        method: 'GET',
        params: {
          ...(params?.studyIcid !== undefined && { study_icid: params.studyIcid }),
        },
      }),
    }),
    getUsers: builder.query<IPagination | IUser[], IUsersParams | void>({
      query: (params: IUsersParams) => ({
        url: '/api/v1/users/',
        method: 'GET',
        params: {
          ...(params?.paginate !== undefined && { paginate: params.paginate }),
        },
      }),
      providesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    getViewPredictionsSummary: builder.query<IViewPredictionsSummary, string>({
      query: (studyLabelingJobUuid: string) => ({
        url: `/api/v1/study-labeling-jobs/${studyLabelingJobUuid}/view-predictions/summary/`,
        method: 'GET',
      }),
    }),
  }),
});
export const {
  useGetFullAnnotationSetsQuery,
  useGetFullAnnotationSetQuery,
  useGetFullAnnotationSetsSearchQuery,
  useLazyGetFullAnnotationSetQuery,
  useGetViewClassesQuery,
  useGetPhaseClassesQuery,
  useGetKeypointCollectionClassesQuery,
  useGetSegmentationClassesQuery,
  useGetStudyLabelingJobsQuery,
  useGetStudyLabelingJobQuery,
  useGetFramesQuery,
  useGetDicomLabelingJobsQuery,
  useGetDicomQuery,
  useGetDicomsQuery,
  useGetViewLabelsQuery,
  useLazyGetDicomLabelingJobsQuery,
  useUpsertViewLabelMutation,
  useUpsertDicomJobMutation,
  useGetPhaseLabelsQuery,
  useUpsertPhaseLabelsMutation,
  useGetKeypointCollectionLabelsQuery,
  useUpsertKeypointCollectionLabelsMutation,
  useUpsertSegmentationLabelsMutation,
  useGetSegmentationLabelQuery,
  useGetSegmentationLabelsQuery,
  useGetReviewFramesQuery,
  useDismissReviewFrameMutation,
  useUpdateReviewFrameStatusMutation,
  useUpsertQualityLabelsMutation,
  useGetQualityLabelsQuery,
  useGetSegmentationPredictionsQuery,
  useGetSegmentationReferencesQuery,
  useGetStudyQuery,
  useGetPnoQuery,
  useGetPnoExistsQuery,
  useGetUsersQuery,
  useGetViewPredictionsSummaryQuery,
} = icardioAPI;
